import { useState } from "react";
import { useSearchParams } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Calendar } from "@/components/ui/calendar";
import { CalendarIcon, X, ArrowRight } from "lucide-react";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { format } from "date-fns";
import { useNotificationTemplates } from "../hooks/use-notifications";

const NOTIFICATION_TYPE_OPTIONS = [
  { value: "ALL", label: "All Types" },
  { value: "SESSION_BOOKING_USER", label: "Session Booking — User" },
  { value: "SESSION_BOOKING_PARTNER", label: "Session Booking — Partner" },
  {
    value: "SESSION_BOOKING_PROFESSIONAL",
    label: "Session Booking — Professional",
  },
  { value: "SESSION_REMINDER_USER", label: "Session Reminder — User" },
  {
    value: "SESSION_REMINDER_PROFESSIONAL",
    label: "Session Reminder — Professional",
  },
  { value: "SESSION_CANCELLATION_USER", label: "Session Cancellation — User" },
  {
    value: "SESSION_CANCELLATION_PARTNER",
    label: "Session Cancellation — Partner",
  },
  {
    value: "SESSION_CANCELLATION_PROFESSIONAL",
    label: "Session Cancellation — Professional",
  },
  { value: "LEAD_REMINDER", label: "Lead Reminder" },
  { value: "PAYMENT_CONFIRMATION", label: "Payment Confirmation" },
  {
    value: "COUPLE_PARTNER_1_SESSION_BOOKING_LINK",
    label: "Couple Partner 1 Booking Link",
  },
  {
    value: "COUPLE_PARTNER_2_SESSION_BOOKING_LINK",
    label: "Couple Partner 2 Booking Link",
  },
  {
    value: "COUPLE_JOINT_SESSSION_BOOKING_LINK",
    label: "Couple Joint Booking Link",
  },
  { value: "TRANSITION_FIRST_SESSION_USER", label: "Transition First — User" },
  {
    value: "TRANSITION_FIRST_SESSION_PROFESSIONAL",
    label: "Transition First — Professional",
  },
  { value: "BUNDLE_REACHOUT", label: "Bundle Reachout" },
  { value: "PAYMENT_REACHOUT", label: "Payment Reachout" },
  { value: "LAB_REACHOUT", label: "Lab Reachout" },
];

const NotificationFilters = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const { data: templatesData } = useNotificationTemplates();

  const tagOptions = [
    { value: "ALL", label: "All Tags" },
    { value: "true", label: "Tags" },
    { value: "false", label: "No Tags" },
  ];

  const statusOptions = [
    { value: "ALL", label: "All Status" },
    { value: "SENT", label: "Sent" },
    { value: "PENDING", label: "Pending" },
    { value: "HOLD", label: "Hold" },
    { value: "FAILED", label: "Failed" },
    { value: "RETRY", label: "Retry" },
  ];
  const currentResolved = searchParams.get("tags") || "ALL";

  const handleTagChange = (value: string) => {
    const newSearchParams = new URLSearchParams(searchParams);
    newSearchParams.set("page", "0");

    if (value === "ALL") {
      newSearchParams.delete("tags");
    } else {
      newSearchParams.set("tags", value);
    }

    setSearchParams(newSearchParams);
  };

  const currentStatus = searchParams.get("status") || "ALL";

  const handleStatusChange = (value: string) => {
    const newSearchParams = new URLSearchParams(searchParams);
    newSearchParams.set("page", "0");
    if (value === "ALL") {
      newSearchParams.delete("status");
    } else {
      newSearchParams.set("status", value);
    }
    setSearchParams(newSearchParams);
  };

  const currentType = searchParams.get("type") || "ALL";

  const handleTypeChange = (value: string) => {
    const newSearchParams = new URLSearchParams(searchParams);
    newSearchParams.set("page", "0");
    if (value === "ALL") {
      newSearchParams.delete("type");
    } else {
      newSearchParams.set("type", value);
    }
    setSearchParams(newSearchParams);
  };

  const currentTemplate = searchParams.get("template") || "ALL";

  const handleTemplateChange = (value: string) => {
    const newSearchParams = new URLSearchParams(searchParams);
    newSearchParams.set("page", "0");
    if (value === "ALL") {
      newSearchParams.delete("template");
    } else {
      newSearchParams.set("template", value);
    }
    setSearchParams(newSearchParams);
  };

  const [dateRange, setDateRange] = useState<{
    start: Date | null;
    end: Date | null;
  }>({
    start: searchParams.get("startDate")
      ? new Date(searchParams.get("startDate")!)
      : null,
    end: searchParams.get("endDate")
      ? new Date(searchParams.get("endDate")!)
      : null,
  });

  const handleDateChange = (range?: { from?: Date; to?: Date }) => {
    setDateRange({ start: range?.from || null, end: range?.to || null });

    // Only trigger API call if both dates are selected
    if (range?.from && range?.to) {
      const newSearchParams = new URLSearchParams(searchParams);
      newSearchParams.set("page", "0");
      newSearchParams.set("startDate", range.from.toISOString());
      // Set end_date to EOD (23:59:59) in ISO string
      const endDate = new Date(range.to);
      endDate.setHours(23, 59, 59, 999);
      newSearchParams.set("endDate", endDate.toISOString());
      setSearchParams(newSearchParams);
    } else {
      // Clear dates if not both selected
      const newSearchParams = new URLSearchParams(searchParams);
      newSearchParams.set("page", "0");
      newSearchParams.delete("startDate");
      newSearchParams.delete("endDate");
      setSearchParams(newSearchParams);
    }
  };

  const handleClearDateRange = () => {
    setDateRange({ start: null, end: null });
    const newSearchParams = new URLSearchParams(searchParams);
    newSearchParams.set("page", "0");
    newSearchParams.delete("startDate");
    newSearchParams.delete("endDate");
    setSearchParams(newSearchParams);
  };

  return (
    <div className="flex gap-2 items-center flex-wrap">
      <Popover>
        <PopoverTrigger asChild>
          <Button
            variant="outline"
            className={`w-60 justify-start text-left font-normal ${
              !dateRange.start && "text-muted-foreground"
            }`}
          >
            <CalendarIcon className="mr-2 h-4 w-4" />
            {dateRange.start && dateRange.end
              ? `${format(dateRange.start, "LLL dd, y")} - ${format(
                  dateRange.end,
                  "LLL dd, y"
                )}`
              : "Pick a date range"}
          </Button>
        </PopoverTrigger>
        <PopoverContent className="w-auto p-4" align="start">
          {/* Two input boxes for start and end dates */}
          <div className="flex items-end gap-3 mb-4 px-4">
            {/* Start Date Box */}
            <div className="flex-1">
              <label className="text-xs text-muted-foreground mb-1.5 block">
                Start
              </label>
              <div className="h-10 px-3 py-2 border border-input bg-background rounded-md flex items-center text-sm">
                {dateRange.start ? (
                  format(dateRange.start, "LLL dd, y")
                ) : (
                  <span className="text-muted-foreground">Select</span>
                )}
              </div>
            </div>

            {/* Arrow */}
            <ArrowRight className="h-4 w-4 text-muted-foreground mb-3" />

            {/* End Date Box */}
            <div className="flex-1">
              <label className="text-xs text-muted-foreground mb-1.5 block">
                End
              </label>
              <div className="h-10 px-3 py-2 border border-input bg-background rounded-md flex items-center text-sm">
                {dateRange.end ? (
                  format(dateRange.end, "LLL dd, y")
                ) : (
                  <span className="text-muted-foreground">Select</span>
                )}
              </div>
            </div>

            {/* Clear button - X icon after end box */}
            {(dateRange.start || dateRange.end) && (
              <Button
                type="button"
                variant="outline"
                size="icon"
                className="h-10 w-10 mb-0 text-muted-foreground hover:text-foreground"
                onClick={handleClearDateRange}
              >
                <X className="h-4 w-4" />
              </Button>
            )}
          </div>

          {/* Calendar below the boxes */}
          <Calendar
            initialFocus
            mode="range"
            selected={{
              from: dateRange.start || undefined,
              to: dateRange.end || undefined,
            }}
            onSelect={handleDateChange}
            numberOfMonths={2}
          />
        </PopoverContent>
      </Popover>

      <Select value={currentStatus} onValueChange={handleStatusChange}>
        <SelectTrigger className="w-32">
          <SelectValue placeholder="Filter by status" />
        </SelectTrigger>
        <SelectContent>
          {statusOptions.map((option) => (
            <SelectItem key={option.value} value={option.value}>
              {option.label}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>

      <Select value={currentType} onValueChange={handleTypeChange}>
        <SelectTrigger className="w-56">
          <SelectValue placeholder="Filter by type" />
        </SelectTrigger>
        <SelectContent>
          {NOTIFICATION_TYPE_OPTIONS.map((option) => (
            <SelectItem key={option.value} value={option.value}>
              {option.label}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>

      <Select value={currentTemplate} onValueChange={handleTemplateChange}>
        <SelectTrigger className="w-56">
          <SelectValue placeholder="Filter by template" />
        </SelectTrigger>
        <SelectContent>
          <SelectItem value="ALL">All Templates</SelectItem>
          {(templatesData?.templates ?? []).map((tpl) => (
            <SelectItem key={tpl} value={tpl}>
              {tpl}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>

      <Select value={currentResolved} onValueChange={handleTagChange}>
        <SelectTrigger className="w-28">
          <SelectValue placeholder="Resolved" />
        </SelectTrigger>
        <SelectContent>
          {tagOptions.map((option) => (
            <SelectItem key={option.value} value={option.value}>
              {option.label}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
    </div>
  );
};

export default NotificationFilters;
